import { Controller } from "@hotwired/stimulus";
import WaveSurfer from "wavesurfer.js";

const PLAY_ICON = `
  <svg width="20" height="20" viewBox="0 0 256 256" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path fill="black" d="M240,128a15.74,15.74,0,0,1-7.6,13.51L88.32,229.65a16,16,0,0,1-16.2.3A15.86,15.86,0,0,1,64,216.13V39.87a15.86,15.86,0,0,1,8.12-13.82,16,16,0,0,1,16.2.3L232.4,114.49A15.74,15.74,0,0,1,240,128Z"/>
  </svg>
`;

const PAUSE_ICON = `
  <svg width="20" height="20" viewBox="0 0 256 256" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path fill="black" d="M216,48V208a16,16,0,0,1-16,16H160a16,16,0,0,1-16-16V48a16,16,0,0,1,16-16h40A16,16,0,0,1,216,48ZM96,32H56A16,16,0,0,0,40,48V208a16,16,0,0,0,16,16H96a16,16,0,0,0,16-16V48A16,16,0,0,0,96,32Z"/>
  </svg>
`;

export default class AudioPlayerController extends Controller {
  static targets = [
    "skeleton",
    "player",
    "waveform",
    "playButton",
    "duration",
    "error"
  ];

  static values = {
    url: String
  };

  handleBeforeCache = () => {
    this.teardownPlayer();
    this.resetUI();
  };

  connect() {
    this.destroyed = false;
    this.isReady = false;
    this.loadFailed = false;
    this.finished = false;
    this.totalDuration = 0;
    this.playToken = 0;

    this.teardownPlayer();
    this.resetUI();

    document.addEventListener("turbo:before-cache", this.handleBeforeCache);

    this.createPlayer();
  }

  disconnect() {
    this.destroyed = true;

    document.removeEventListener("turbo:before-cache", this.handleBeforeCache);

    this.teardownPlayer();

    const url = this.urlValue;
    if (url?.startsWith("blob:")) {
      const element = this.element;
      setTimeout(() => {
        if (!element.isConnected) globalThis.URL.revokeObjectURL(url);
      }, 0);
    }
  }

  createPlayer() {
    const player = WaveSurfer.create({
      container: this.waveformTarget,
      height: 36,
      barWidth: 3,
      barGap: 2,
      barRadius: 2,
      normalize: true,
      waveColor: "#9a7a9c",
      progressColor: "#f773c5",
      cursorWidth: 0
    });

    this.player = player;

    const isCurrent = () => !this.destroyed && this.player === player;

    player.on("error", (error) => {
      if (isCurrent()) this.showError(error);
    });

    player.on("ready", () => {
      if (!isCurrent()) return;

      this.isReady = true;
      this.totalDuration = player.getDuration();
      this.durationTarget.textContent = this.format(this.totalDuration);

      this.skeletonTarget.classList.add("d-none");
      this.playerTarget.classList.remove("d-none");
    });

    player.on("timeupdate", (currentTime) => {
      if (!isCurrent()) return;

      this.durationTarget.textContent =
        `${this.format(currentTime)} / ${this.format(this.totalDuration)}`;
    });

    player.on("play", () => {
      if (!isCurrent()) return;

      const active = AudioPlayerController.activePlayer;
      if (active && active !== this) active.stop();

      AudioPlayerController.activePlayer = this;
      this.finished = false;
      this.setPlaying(true);
    });

    player.on("pause", () => {
      if (!isCurrent()) return;

      this.setPlaying(false);
      this.releaseActive();
    });

    player.on("finish", () => {
      if (!isCurrent()) return;

      this.finished = true;
      player.pause();
      player.setTime(0);

      this.setPlaying(false);
      this.releaseActive();

      this.durationTarget.textContent = this.format(this.totalDuration);
    });

    player.load(this.urlValue).catch((error) => {
      if (isCurrent()) this.showError(error);
    });
  }

  teardownPlayer() {
    const player = this.player;
    if (!player) return;

    this.player = null;
    this.playToken++;
    this.isReady = false;

    this.releaseActive(this);

    try {
      player.pause();
      const media = player.getMediaElement?.();
      player.destroy();

      if (media) {
        media.removeAttribute("src");
        media.load();
      }
    } catch (error) {
      console.warn("Error while destroying audio player", error);
    }
  }


  toggle() {
    const player = this.player;

    if (!player || this.destroyed || this.loadFailed || !this.isReady) return;

    if (player.isPlaying()) {
      player.pause();
      return;
    }

    if (
      this.finished ||
      (this.totalDuration > 0 &&
        player.getCurrentTime() >= this.totalDuration - 0.05)
    ) {
      player.setTime(0);
      this.finished = false;
    }

    const token = ++this.playToken;

    player.play().catch((error) => {
      if (this.player !== player || token !== this.playToken) return;

      if (error?.name === "AbortError") return;

      console.error("Unable to play audio", error);

      this.setPlaying(false);
      this.releaseActive();
    });
  }

  stop() {
    const player = this.player;
    if (!player || this.destroyed) return;

    this.playToken++;

    player.pause();
    player.setTime(0);

    this.finished = false;
    this.setPlaying(false);

    if (this.hasDurationTarget) {
      this.durationTarget.textContent =
        `0:00 / ${this.format(this.totalDuration)}`;
    }

    this.releaseActive();
  }

  setPlaying(playing) {
    if (!this.hasPlayButtonTarget) return;
    this.playButtonTarget.innerHTML = playing ? PAUSE_ICON : PLAY_ICON;
  }

  releaseActive(owner = this) {
    if (AudioPlayerController.activePlayer === owner) {
      AudioPlayerController.activePlayer = null;
    }
  }

  resetUI() {
    if (this.hasSkeletonTarget) this.skeletonTarget.classList.remove("d-none");
    if (this.hasPlayerTarget) this.playerTarget.classList.add("d-none");
    if (this.hasErrorTarget) this.errorTarget.classList.add("d-none");
    this.setPlaying(false);
    if (this.hasDurationTarget) this.durationTarget.textContent = "0:00";
  }

  showError(error) {
    if (this.loadFailed || this.destroyed) return;

    this.loadFailed = true;

    console.error("Unable to load audio", error);

    this.skeletonTarget.classList.add("d-none");
    this.playerTarget.classList.add("d-none");
    this.errorTarget.classList.remove("d-none");
  }

  format(seconds) {
    if (!seconds || Number.isNaN(seconds)) {
      return "0:00";
    }

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);

    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  }
}

AudioPlayerController.activePlayer = null;
